import { useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { X } from "lucide-react";

import { Calendar } from "src/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";
import { buttonVariants } from "src/components/ui/button";
import { cn } from "src/lib/utils";

/** `yyyy-MM-dd`, the format the list endpoints expect. */
const PARAM_FORMAT = "yyyy-MM-dd";

const parseParam = (value: string | null) => (value ? parseISO(value) : undefined);

/**
 * Date-range filter backed by the `start_date` / `end_date` search params, so the
 * selection survives a reload and is shareable as a link.
 */
const CustomDateRange = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const startDate = searchParams.get("start_date");
	const endDate = searchParams.get("end_date");

	const setParam = (params: URLSearchParams, key: string, date?: Date) => {
		if (date) params.set(key, format(date, PARAM_FORMAT));
		else params.delete(key);
	};

	const clearDateFilters = () => {
		const next = new URLSearchParams(searchParams);
		next.delete("start_date");
		next.delete("end_date");
		setSearchParams(next);
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				<span
					className={cn(
						buttonVariants({ variant: "ghost" }),
						"tw-min-w-[220px] tw-gap-3 tw-rounded-[10px] tw-border tw-border-border tw-bg-white tw-px-3 !tw-py-2 tw-text-[13.5px] tw-font-normal tw-text-text-soft !tw-justify-between hover:tw-border-teal"
					)}
				>
					{startDate ? (
						<span>
							{format(parseISO(startDate), "PPP")}
							{endDate && ` - ${format(parseISO(endDate), "PPP")}`}
						</span>
					) : (
						<span>Select date</span>
					)}

					{(startDate || endDate) && (
						<button type="button" aria-label="Clear date filter" className="tw-p-1.5" onClick={clearDateFilters}>
							<X className="!tw-size-4 tw-stroke-red" />
						</button>
					)}
				</span>
			</PopoverTrigger>

			<PopoverContent className="tw-w-auto tw-p-0" align="start">
				<Calendar
					mode="range"
					selected={{ from: parseParam(startDate), to: parseParam(endDate) }}
					onSelect={(range) => {
						const next = new URLSearchParams(searchParams);

						setParam(next, "start_date", range?.from);
						setParam(next, "end_date", range?.to);

						setSearchParams(next);
					}}
					numberOfMonths={2}
				/>
			</PopoverContent>
		</Popover>
	);
};

export default CustomDateRange;
